import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContactSection = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Handle File Selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if number files is greater than 5
    if (files.length > 5) {
      toast.error(`Number of files exceeded. Please select fewer files.`);
      return false;
    }

    // Validate files
    const validFiles = files.filter(file => {
      // Check file type
      const allowedTypes = ["image/jpeg", "image/png", "video/mp4"];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only JPG, PNG images and MP4 videos are allowed!`);
        return false;
      }
      
      // Check file size (Max: 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File size must be under 10MB!`);
        return false;
      }
      
      return true;
    });
    
    // Map files to objects with preview URLs
    const previews = validFiles.map((file) => {
      return {
        file,
        url: URL.createObjectURL(file),
      };
    });
  
    setSelectedFiles(previews);
  };

  // Remove a file from selected files
  const removeFile = (index) => {
    setSelectedFiles(prev => {
      // Revoke the object URL to prevent memory leaks
      URL.revokeObjectURL(prev[index].url);
      const newFiles = prev.filter((_, i) => i !== index);
      
      // If no files remain, reset the file input
      if (newFiles.length === 0) {
        resetFileInput();
      }
      
      return newFiles;
    });
  };

  // Reset the file input element
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove all files
  const removeAllFiles = () => {
    // Clean up object URLs
    selectedFiles.forEach(fileObj => URL.revokeObjectURL(fileObj.url));
    setSelectedFiles([]);
    resetFileInput();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !message) {
      toast.error("Please fill in all fields.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("message", message);
    
    // Append all files to the form data
    selectedFiles.forEach(fileObj => {
      formData.append("files", fileObj.file);
    });

    try {
      const response = await fetch("https://max-hauling.com/api/send-email", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Email sent successfully!");
        setName("");
        setEmail("");
        setMessage("");
        
        // Clean up files and reset the input
        removeAllFiles();
      } else {
        toast.error("Failed to send email.");
      }

      // console.log('Full server response:', data);

      if (data.success) {
        toast.success("Email sent successfully!");
        // ... rest of your success handling
      } else {
        console.error('Server error details:', data);
        toast.error(data.message || "Failed to send email.");
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Error sending email.");
    }
  };

  return (
    <section id="contact" className="py-12 bg-white shadow-md rounded-md">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-semibold" data-aos="fade-down">Contact Me</h2>
        <p>If you have any questions or need my services, please contact me:</p>
        
        <div className="flex flex-col items-left container mb-4">
          {/* Contact Information */}
          <div className="mt-2 bg-white p-4 mb-2 rounded-lg shadow-md inline-block w-full md:w-1/2" data-aos="fade-left">
            <p className="text-lg font-semibold"> Email: <b>maximum</b>hauling@gmail.com</p>
            <p className="text-lg font-semibold"> Phone: (530) 433-4160</p>
          </div>

          {/* Business Hours */}
          <div className="mb-4 bg-blue-500 text-white p-4 rounded-lg inline-block shadow-md w-full md:w-1/2" data-aos="fade-left">
            <p className="text-lg font-semibold"> Business Hours:</p>
            <p>I am currently available <strong>weekdays</strong> from <strong>10 AM to 4 PM</strong> and <strong>Saturdays</strong> from <strong>9 AM to 5 PM</strong>.</p>
          </div>
        </div>

        {/* Contact Form */}
        <form className="bg-white p-6 rounded-lg shadow-md inline-block w-full md:w-1/2" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Your Name"
            className="w-full p-2 border rounded mb-4"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Your Email"
            className="w-full p-2 border rounded mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <textarea
            placeholder="Your Message"
            className="w-full p-2 border rounded mb-4"
            rows="4"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          ></textarea>

          {/* Multiple File Upload Field with Hidden Default Text */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              Attach Images or Videos (up to 5):
            </label>
            
            {/* Custom file input container with hidden text */}
            <div className="relative">
              {/* This creates a wrapper that hides the overflow text */}
              <div className="overflow-hidden relative flex items-center">
                {/* This creates a custom button */}
                <label className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded cursor-pointer inline-block">
                  Choose Files
                  {/* The actual file input is positioned absolutely and made invisible */}
                  <input
                    type="file"
                    accept="image/jpeg, image/png, video/mp4"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    multiple
                    ref={fileInputRef}
                  />
                </label>
                
                {/* Our own custom status text */}
                {selectedFiles.length > 0 && (
                  <span className="ml-3 text-sm text-gray-600">
                    {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected
                  </span>
                )}
                
                
              </div>
              <div className="mt-2">
                {/* Clear button */}
                {selectedFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={removeAllFiles}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded cursor-pointer inline-block"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mt-1">Maximum 5 files, 10MB each. JPG, PNG, or MP4 only.</p>
          </div>

          {/* File Previews */}
          {selectedFiles.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Selected Files ({selectedFiles.length}):</p>
              <div className="flex flex-wrap gap-4">
                {selectedFiles.map((fileObj, index) => (
                  <div key={index} className="relative">
                    <div className="w-24 h-24 border rounded-lg p-1 shadow-sm">
                      {fileObj.file.type.startsWith("image/") ? (
                        <img 
                          src={fileObj.url} 
                          alt="preview" 
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <video controls className="w-full h-full rounded">
                          <source src={fileObj.url} type={fileObj.file.type} />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                    <button 
                      type="button" 
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      onClick={() => removeFile(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition"
          >
            Send Message
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;